import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useState } from "react";
import { PlanningProvider, usePlanning } from "../context/PlanningContext";
import { SlideOperationsProvider } from "../context/InsertedSlidesContext";
import { ChangeTrackingProvider } from "../context/ChangeTrackingContext";
import { ConversationProvider, useConversation, } from "../../../context/ConversationContext";
import { usePlanningInitialize } from "../hooks/usePlanningInitialize";
import StartNewPlan from "./StartNewPlan";
import PlanProgress from "./PlanProgress";
import DetailedPlanning from "./DetailedPlanning";
import { BuildProgressDetailView, } from "./builds";
import { useBuildJobs } from "../hooks/useBuildJobs";
import { authService } from "../../../services/authService";
import { setMessageService, } from "../../../hooks/useSendMessage";
import { v4 as uuidv4 } from "uuid";
// @ts-ignore
import dotsAnimation from "../../../../assets/dots.json";
const plannerWindow = window;
// Component that initializes message service (similar to App.tsx ChatPanelWithInit)
const PlanningAppWithInit = () => {
    const { setConversationId } = useConversation();
    const [isInitializing, setIsInitializing] = React.useState(true);
    const [isReady, setIsReady] = React.useState(false);
    const isReadyRef = React.useRef(false);
    const isInitializingRef = React.useRef(false);
    React.useEffect(() => {
        console.log("[PlanningApp] useEffect running...");
        // Set conversation ID immediately so ChatPanel can work
        const initialConversationId = uuidv4();
        setConversationId(initialConversationId);
        console.log("[PlanningApp] Set initial conversation ID:", initialConversationId);
        const checkAndInitialize = async () => {
            if (isInitializingRef.current) {
                console.log("[PlanningApp] Already initializing, skipping...");
                return;
            }
            console.log("[PlanningApp] Starting checkAndInitialize...");
            try {
                await authService.reloadFromStorage();
                const authenticated = authService.isAuthenticated();
                const token = authService.getAccessToken();
                console.log("[PlanningApp] Auth check:", {
                    authenticated,
                    hasToken: !!token,
                });
                if (authenticated && token) {
                    if (!isReadyRef.current) {
                        isInitializingRef.current = true;
                        setIsInitializing(true);
                        try {
                            // Try to access planner service from window (set by ppt-addin)
                            // According to Notion docs: Planning Modal instantiates Planner Agent (100% exclusive usage)
                            // Wait a bit for functions to be available (they're set in Office.onReady)
                            // Type-safe access thanks to window.d.ts type definitions
                            let initializePlannerService = plannerWindow.__initializePlannerService;
                            let sendMessageToPlanner = plannerWindow.__sendMessageToPlanner;
                            // Retry up to 5 times with 100ms delay if functions not found
                            let retries = 0;
                            while ((!initializePlannerService || !sendMessageToPlanner) &&
                                retries < 5) {
                                console.log(`[PlanningApp] Waiting for planner service functions (attempt ${retries + 1}/5)...`);
                                await new Promise((resolve) => setTimeout(resolve, 100));
                                initializePlannerService =
                                    plannerWindow.__initializePlannerService;
                                sendMessageToPlanner = plannerWindow.__sendMessageToPlanner;
                                retries++;
                            }
                            console.log("[PlanningApp] Checking for planner service functions:", {
                                hasInitialize: !!initializePlannerService,
                                hasSendMessage: !!sendMessageToPlanner,
                                windowKeys: Object.keys(window).filter((k) => k.startsWith("__")),
                            });
                            if (initializePlannerService && sendMessageToPlanner) {
                                console.log("[PlanningApp] Found planner service functions, initializing...");
                                await initializePlannerService();
                                const service = {
                                    sendMessage: async (message, _conversationId, onChunk) => {
                                        const response = await sendMessageToPlanner(message, undefined, {
                                            onChunk,
                                            onPlanChange: (plan, action) => {
                                                console.log(`[PlanningApp] Plan ${action}, notifying subscribers...`);
                                                // Notify all registered callbacks about the plan update
                                                if (plannerWindow.__notifyPlanUpdate) {
                                                    plannerWindow.__notifyPlanUpdate(plan, action);
                                                }
                                            },
                                        });
                                        return { content: response.content };
                                    },
                                    initialize: async () => {
                                        await initializePlannerService();
                                    },
                                };
                                setMessageService(service);
                                isReadyRef.current = true;
                                setIsReady(true);
                                console.log("[PlanningApp] Planner service initialized successfully");
                            }
                            else {
                                console.warn("[PlanningApp] Planner service functions not available on window");
                                // Fallback: set a dummy service that shows error
                                setMessageService({
                                    sendMessage: async () => {
                                        throw new Error("Message service not initialized. Please ensure the planner service is available.");
                                    },
                                });
                                isReadyRef.current = true;
                                setIsReady(true);
                            }
                        }
                        catch (err) {
                            console.error("[PlanningApp] Failed to initialize message service:", err);
                            isReadyRef.current = false;
                            setIsReady(false);
                        }
                        finally {
                            setIsInitializing(false);
                            isInitializingRef.current = false;
                        }
                    }
                }
                else {
                    console.log("[PlanningApp] Not authenticated yet, will wait for auth hydration");
                }
            }
            catch (err) {
                console.error("[PlanningApp] Error checking auth:", err);
                setIsInitializing(false);
                isInitializingRef.current = false;
            }
        };
        // Initial check
        checkAndInitialize();
        // Listen for auth changes and retry initialization when auth becomes available
        const unsubscribe = authService.onAuthChange(async (isAuth, user) => {
            console.log("[PlanningApp] Auth change detected:", {
                isAuth,
                userEmail: user?.email,
            });
            if (isAuth && !isReadyRef.current) {
                console.log("[PlanningApp] Auth available, retrying initialization...");
                await checkAndInitialize();
            }
        });
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [setConversationId]);
    if (isInitializing) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("div", { className: "text-center", children: _jsx("div", { className: "text-gray-600 mb-2", children: "Initializing..." }) }) }));
    }
    return _jsx(PlanningAppContent, {});
};
const PlanningAppContent = () => {
    usePlanningInitialize();
    const { loadPlan, setCurrentPlanId } = usePlanning();
    const [view, setView] = useState("start");
    const [planData, setPlanData] = useState({
        slideCount: "",
        styleGuide: null,
        description: "",
    });
    const [initialPlan, setInitialPlan] = useState(null);
    const [savedVersions, setSavedVersions] = useState(undefined);
    const [isResumingPlan, setIsResumingPlan] = useState(false);
    // Build jobs state
    const [selectedBuildId, setSelectedBuildId] = useState(null);
    const { jobs: buildJobs, messages: allBuildMessages, pauseBuild, resumeBuild, cancelBuild, getMessages, } = useBuildJobs();
    // Get the selected build job
    const selectedBuild = buildJobs.find((j) => j.planId === selectedBuildId) || null;
    // Get messages for selected build - now reactive to updates!
    const buildMessages = selectedBuildId
        ? allBuildMessages[selectedBuildId] || []
        : [];
    // Handle viewing build detail
    const handleViewBuildDetail = (planId) => {
        setSelectedBuildId(planId);
        setView("build-detail");
    };
    // Handle back from build detail
    const handleBackFromBuildDetail = () => {
        setSelectedBuildId(null);
        setView("start");
    };
    const handlePlanComplete = (data, plan) => {
        setPlanData(data);
        setInitialPlan(plan || null);
        setSavedVersions(undefined); // Clear saved versions for new plans
        setView("detailed-planning");
    };
    // Handle resuming a saved plan
    const handleResumePlan = async (planId) => {
        setIsResumingPlan(true);
        console.log("[PlanningApp] Resuming plan:", planId);
        try {
            const plan = await loadPlan(planId);
            // Log full plan object for debugging
            console.log("[PlanningApp] Full plan object from API:", JSON.stringify(plan, null, 2));
            if (plan && plan.plan_versions.length > 0) {
                // Get the latest version
                const latestVersion = plan.plan_versions[plan.plan_versions.length - 1];
                // Cast through unknown to handle the Record<string, unknown> type
                const planJson = latestVersion.plan_json;
                // Log plan_json content for debugging
                console.log("[PlanningApp] Raw plan_json from latest version:", latestVersion.plan_json);
                console.log("[PlanningApp] plan_json fields:", {
                    slideCount: planJson.slideCount,
                    totalSlides: planJson.totalSlides,
                    prompt: planJson.prompt,
                    sectionsCount: planJson.sections?.length,
                });
                // Log style guide fields specifically
                console.log("[PlanningApp] Style guide fields from plan object:", {
                    style_guide_deck_id: plan.style_guide_deck_id,
                    style_guide_presentation_path: plan.style_guide_presentation_path,
                    style_guide_presentation_name: plan.style_guide_presentation_name,
                });
                // Extract slide count - check multiple possible fields
                // Priority: saved slideCount > totalSlides > plan.target_slides
                const slideCount = planJson.slideCount ||
                    planJson.totalSlides ||
                    plan.target_slides ||
                    0;
                // Extract style guide info from the Plan object (saved by backend)
                // The style guide info is stored at the plan level, not in plan_json
                let styleGuideSelection = null;
                const hasStyleGuide = plan.style_guide_deck_id || plan.style_guide_presentation_path;
                console.log("[PlanningApp] hasStyleGuide:", hasStyleGuide);
                if (hasStyleGuide) {
                    // Determine type: if there's a path, it's uploaded; otherwise it's live
                    const isUploaded = !!plan.style_guide_presentation_path;
                    styleGuideSelection = {
                        type: isUploaded ? "uploaded" : "live",
                        documentId: plan.style_guide_deck_id || "",
                        label: plan.style_guide_presentation_name ||
                            plan.style_guide_presentation_path?.split(/[/\\]/).pop() ||
                            "Style Guide",
                        path: plan.style_guide_presentation_path || undefined,
                    };
                    console.log("[PlanningApp] Built styleGuideSelection:", styleGuideSelection);
                }
                // Extract description from plan_json - prioritize user's prompt over LLM-generated description
                // prompt = user's original input, description = LLM-generated plan title
                const description = planJson.prompt ||
                    planJson.description ||
                    planJson.sections?.[0]?.name ||
                    "Resumed Plan";
                console.log("[PlanningApp] Resuming with final data:", {
                    slideCount,
                    styleGuide: styleGuideSelection,
                    description,
                    planVersions: plan.plan_versions.length,
                });
                // Set plan data from the saved plan
                setPlanData({
                    slideCount: String(slideCount),
                    styleGuide: styleGuideSelection,
                    description,
                });
                setInitialPlan(planJson);
                setSavedVersions(plan.plan_versions);
                setCurrentPlanId(planId);
                setView("detailed-planning");
            }
            else {
                console.error("[PlanningApp] Plan has no versions:", planId);
            }
        }
        catch (error) {
            console.error("[PlanningApp] Error resuming plan:", error);
        }
        finally {
            setIsResumingPlan(false);
        }
    };
    if (isResumingPlan) {
        return (_jsx("div", { className: "flex items-center justify-center h-screen bg-gray-100", children: _jsxs("div", { className: "flex items-center gap-1 text-gray-600 text-[13px]", children: ["Loading plan", _jsx("lottie-player", { src: JSON.stringify(dotsAnimation), background: "transparent", speed: "1", style: { width: "24px", height: "24px" }, loop: true, autoplay: true })] }) }));
    }
    return (_jsx("div", { className: "h-screen w-full overflow-hidden bg-gray-100", children: view === "start" ? (
        /* Start Screen */
        _jsx("div", { className: "flex w-full h-full flex-col items-start bg-gray-100 p-8 overflow-y-auto", children: _jsx(StartNewPlan, { onStartNewPlan: () => setView("progress"), onResumePlan: handleResumePlan, onViewBuildDetail: handleViewBuildDetail }) })) : view === "progress" ? (
        /* Progress View */
        _jsx(PlanProgress, { onBack: () => setView("start"), onComplete: handlePlanComplete })) : view === "build-detail" && selectedBuild ? (
        /* Build Progress Detail View */
        _jsx(BuildProgressDetailView, { job: selectedBuild, messages: buildMessages, onBack: handleBackFromBuildDetail, onPause: pauseBuild, onResume: resumeBuild, onCancel: cancelBuild })) : (
        /* Detailed Planning View */
        _jsx(DetailedPlanning, { planData: planData, initialPrompt: planData.description, initialPlan: initialPlan, savedVersions: savedVersions, onBack: () => setView("progress"), onBuildQueued: () => setView("start") })) }));
};
const PlanningApp = () => {
    return (_jsx(PlanningProvider, { children: _jsx(SlideOperationsProvider, { children: _jsx(ChangeTrackingProvider, { children: _jsx(ConversationProvider, { children: _jsx(PlanningAppWithInit, {}) }) }) }) }));
};
export default PlanningApp;
